import React, { useEffect, useState, useMemo, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DropdownNoLabel from "../DropDown/DropdownNoLabel";
import { CLOSE_TAGGING_DROPDOWNS } from "../DropDown/TaggingDropdown/TaggingDropdown";
import { useGlobalContext } from "../../Context/GlobalContext";
import {
	convertTo12Hour,
	convertTo24HourFormat,
	calculateEndTime,
	formatDateToDDMMYYYY,
	convertToMinutes,
	extractHHMM,
	buildTimeSelection as buildCanonicalTimeSelection,
	getTimeSelectionDayOffset,
	normalizeTimeSelectionObject as normalizeCanonicalTimeSelectionObject,
	getOutletExtendedFlagForDate,
	findMatchingTimeOption,
	stripTimeSelectionDayOffsetSuffix,
	resolveNearestTimeSelection,
	getViewRelativeAbsoluteStartMinutes,
	isServiceEligibleForCrossDayTimeSelection,
} from "../../Utils/utils";
import {
	scheduleServiceMove,
	resolveServiceScheduleKey,
	ensureServiceLinkedFlags,
} from "../../Utils/serviceScheduleChain";
import styles from "../RightSidebar/rightsidebar.module.css";
import { getBookingPreventChanges } from "../../Utils/BookingPreventChanges";
// Removed UseTimeOptions import - using static time options from 12:00 AM to 12:00 PM

const DateTimeOutletSelector = ({
	initialDate,
	initialTime,
	onDateChange,
	onTimeChange,
	fromEdit = false,
	onPaymentInProgressAttempt,
}) => {
	const {
		outletList,
		bookingData,
		setBookingData,
		formErrors,
		setItemData,
		selectedOutlet,
		setEditedItemData,
		setBookingChanged,
		selectedTime, setSelectedTime,
		selectedOutletName,
		selectedDate, setSelectedDate,
		selectedOutletData,
		selectedServices,
		setSelectedServices,
		paxes,
		setPaxes,
		setIsDateChange,
		setIsTimeChange,
		setHitTemporaryBookingApi,
		isMembershipApplied,
		publicHoliday,
		calendarDateSyncSignal,
		setCalendarDateSyncSignal,
		dateSyncState,
		setDateSyncState,
		editableBooking,
	} = useGlobalContext();

	const { isBookingCompleted, isBookingPaymentInProgress} = getBookingPreventChanges(editableBooking);

	const [selectedOutletTypeID, setSelectedOutletTypeID] = useState(null);
	const [showCalendar, setShowCalendar] = useState(false);
	const [localSelectedTime, setLocalSelectedTime] = useState(selectedTime || null);
	const [didInitFromEdit, setDidInitFromEdit] = useState(false);
	const [lastStableDisplayedTime, setLastStableDisplayedTime] = useState("");

	const closeTaggingDropdowns = () => {
		window.dispatchEvent(new Event(CLOSE_TAGGING_DROPDOWNS));
	};

	const normalizeDisplayTime = (timeValue) => {
		if (!timeValue) return "";
		if (typeof timeValue === "string" && /am|pm/i.test(timeValue)) return timeValue;
		return convertTo12Hour(timeValue);
	};

	const normalizeTimeValue = (timeValue) => {
		if (!timeValue) return "";
		if (typeof timeValue === "string" && /am|pm/i.test(timeValue)) {
			return convertTo24HourFormat(timeValue);
		}
		return extractHHMM(timeValue);
	};

	const getTimeDayOffset = (timeLike) => getTimeSelectionDayOffset(timeLike);

	const formatTimeLabelForDisplay = (label = "") =>
		stripTimeSelectionDayOffsetSuffix(label);

 	const buildTimeSelection = (timeValue, dayOffset = 0, explicitLabel = "", absoluteMinutes = null) => {
		const normalizedValue = normalizeTimeValue(timeValue);
		const built = buildCanonicalTimeSelection(
			normalizedValue,
			dayOffset,
			explicitLabel || normalizeDisplayTime(normalizedValue),
			absoluteMinutes
		);
		if (!built) return null;
		return {
			...built,
			label: formatTimeLabelForDisplay(built.label),
		};
	};

	const normalizeTimeSelectionObject = (timeLike) => {
		const normalized = normalizeCanonicalTimeSelectionObject(timeLike);
		if (!normalized) return null;
		return {
			...normalized,
			label: formatTimeLabelForDisplay(normalized.label),
		};
	};

	const logEditDateDebug = () => {};


	// Helper function to check if service time is before 6pm (18:00)
	const isTimeBefore6PM = (startTime) => {
		if (!startTime) return false;
		const startMinutes = convertToMinutes(startTime);
		const sixPM = 18 * 60; // 18:00 in minutes
		return startMinutes < sixPM;
	};

	// Helper function to normalize date input
	const normalizeDateInput = (input) => {
		if (!input) return null;
		if (input instanceof Date) return input;
		if (typeof input === "string") {
			if (/^\d{2}-\d{2}-\d{4}$/.test(input)) {
				const [dd, mm, yyyy] = input.split("-");
				return new Date(`${yyyy}-${mm}-${dd}`);
			}
			if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
				return new Date(input);
			}
		}
		const parsed = new Date(input);
		return isNaN(parsed) ? null : parsed;
	};

	// Helper function to check if a date is a weekday (Monday-Friday)
	const isWeekday = (dateInput) => {
		if (!dateInput) return false;
		const normalizedDate = normalizeDateInput(dateInput);
		if (!normalizedDate) return false;
		const dayOfWeek = normalizedDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
		return dayOfWeek >= 1 && dayOfWeek <= 5; // Monday (1) through Friday (5)
	};

	// Helper function to get the correct price based on isMemberRateApplied
	const getServicePriceFromPriceRate = (service, isMemberRateAppliedValue) => {
		// Try to get price_rate from service, or from service list cache
		let priceRate = service?.price_rate;
		
		// If price_rate is not available, try to get it from service list cache
		if (!priceRate && service?.id) {
			try {
				const ss = sessionStorage.getItem('serviceList');
				const categories = ss ? JSON.parse(ss) : [];
				const serviceId = Number(service.id || service.service);
				
				// Find the service in the cached list
				for (const cat of categories || []) {
					const foundService = (cat?.services || []).find(svc => Number(svc?.id) === serviceId);
					if (foundService?.price_rate) {
						priceRate = foundService.price_rate;
						break;
					}
				}
			} catch (e) {
				// Ignore errors when accessing cache
			}
		}
		
		if (!priceRate) {
			// Fallback to old logic if price_rate is not available
			return isMembershipApplied ? (service.member_price || service.price) : service.price;
		}
		
		if (isMemberRateAppliedValue) {
			// Use member_price_before_6_pm if isMemberRateApplied is true
			return priceRate.member_price_before_6_pm || priceRate.memberPrice || service.member_price || service.price;
		} else if (isMembershipApplied) {
			// Use member_after_6_or_weekends if isMemberRateApplied is false but isMembershipApplied is true
			return priceRate.member_after_6_or_weekends || priceRate.memberPrice || service.member_price || service.price;
		} else {
			// Use nonMemberPrice otherwise
			return priceRate.nonMemberPrice || service.price;
		}
	};

	// Generate same-day time options for all outlets, and append early-morning
	// times only when the selected outlet is expanded for the selected date.
	const generateTimeOptions = (includeNextDay = false) => {
		const options = [];
		for (let hour = 0; hour < 24; hour++) {
			for (let minute = 0; minute < 60; minute += 5) {
				const minuteStr = String(minute).padStart(2, '0');
				
				// Convert to 12-hour format for display
				let hourFormatted;
				let ampm;
				
				if (hour === 0) {
					hourFormatted = 12;
					ampm = 'AM';
				} else if (hour === 12) {
					hourFormatted = 12;
					ampm = 'PM';
				} else if (hour < 12) {
					hourFormatted = hour;
					ampm = 'AM';
				} else {
					hourFormatted = hour - 12;
					ampm = 'PM';
				}
				
				const label = `${hourFormatted}:${minuteStr} ${ampm}`;
				
				options.push({
					label,
					value: label,
					dayOffset: 0,
				});
			}
		}

		if (includeNextDay) {
			for (let hour = 0; hour < 6; hour++) {
				for (let minute = 0; minute < 60; minute += 5) {
					const minuteStr = String(minute).padStart(2, '0');
					const hourFormatted = hour === 0 ? 12 : hour;
					const label = `${hourFormatted}:${minuteStr} AM`;
					options.push({
						label,
						value: label,
						dayOffset: 1,
					});
				}
			}
		}
		
		return options;
	};

	const getSelectedDateObject = () => {
		if (selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())) {
			return new Date(selectedDate);
		}
		if (selectedDate) {
			const parsed = new Date(selectedDate);
			if (!Number.isNaN(parsed.getTime())) {
				return parsed;
			}
		}
		if (bookingData?.date) {
			const parsed = new Date(bookingData.date);
			if (!Number.isNaN(parsed.getTime())) {
				return parsed;
			}
		}
		return new Date();
	};

	const formatSelectedDateString = () => formatDateToDDMMYYYY(getSelectedDateObject());

	const parseDateString = (dateStr) => {
		if (!dateStr || typeof dateStr !== "string") return null;
		const [dd, mm, yyyy] = dateStr.split("-");
		if (!dd || !mm || !yyyy) return null;
		const parsed = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	};

	const normalizeDateString = (dateStr) => {
		const parsed = parseDateString(dateStr);
		if (parsed) return formatDateToDDMMYYYY(parsed);
		return formatSelectedDateString();
	};

	const addDaysToDateString = (dateStr, days = 1) => {
		const parsed = parseDateString(dateStr) || getSelectedDateObject();
		const clone = new Date(parsed);
		clone.setDate(clone.getDate() + Number(days || 0));
		return formatDateToDDMMYYYY(clone);
	};

	const getServiceAtDateForSelection = (baseDate, timeLike) => {
		const baseDateStr = baseDate instanceof Date
			? formatDateToDDMMYYYY(baseDate)
			: normalizeDateString(baseDate);
		return addDaysToDateString(baseDateStr, getTimeDayOffset(timeLike));
	};

	const getDateDayOffset = (dateStr) => {
		const baseDate = parseDateString(formatSelectedDateString());
		const targetDate = parseDateString(dateStr);
		if (!baseDate || !targetDate) return 0;
		const base = new Date(baseDate);
		const target = new Date(targetDate);
		base.setHours(0, 0, 0, 0);
		target.setHours(0, 0, 0, 0);
		return Math.max(0, Math.round((target.getTime() - base.getTime()) / (24 * 60 * 60 * 1000)));
	};

	const getDateDayOffsetFromBase = (baseDateStr, targetDateStr) => {
		const baseDate = parseDateString(baseDateStr);
		const targetDate = parseDateString(targetDateStr);
		if (!baseDate || !targetDate) return 0;
		const base = new Date(baseDate);
		const target = new Date(targetDate);
		base.setHours(0, 0, 0, 0);
		target.setHours(0, 0, 0, 0);
		return Math.max(0, Math.round((target.getTime() - base.getTime()) / (24 * 60 * 60 * 1000)));
	};

	const buildServiceTimelineMeta = (startDateStr, timeValue, baseDateStr = null) => {
		const normalizedTimeValue = normalizeTimeValue(timeValue);
		const resolvedBaseDateStr = normalizeDateString(baseDateStr || formatSelectedDateString());
		const dayOffset = getDateDayOffsetFromBase(resolvedBaseDateStr, startDateStr);
		const absoluteMinutes = normalizedTimeValue
			? (dayOffset * 1440) + convertToMinutes(normalizedTimeValue)
			: null;
		return {
			dayOffset,
			absoluteMinutes,
		};
	};

	const getSelectedDayCrossDaySegmentPosition = (serviceLike, baseDateStr = formatSelectedDateString()) => {
		const normalizedBaseDate = normalizeDateString(baseDateStr);
		const segments = Array.isArray(serviceLike?.cross_day_booking_item)
			? serviceLike.cross_day_booking_item
			: [];
		if (!normalizedBaseDate || segments.length < 2) return null;
		const firstStartMinutes = convertToMinutes(extractHHMM(segments[0]?.start_time || ""));
		if (Number.isNaN(firstStartMinutes) || firstStartMinutes >= (6 * 60)) return null;
		const firstDate = normalizeDateString(segments[0]?.start_date);
		const secondDate = normalizeDateString(segments[1]?.start_date);
		if (firstDate === normalizedBaseDate) return "first";
		if (secondDate === normalizedBaseDate) return "second";
		return null;
	};

	const getServiceDisplayDayOffset = (serviceLike, baseDateStr = formatSelectedDateString()) => {
		const segmentPosition = getSelectedDayCrossDaySegmentPosition(serviceLike, baseDateStr);
		if (segmentPosition === "first") return 1;
		if (segmentPosition === "second") return 0;
		if (typeof serviceLike?.dayOffset === "number" && !Number.isNaN(serviceLike.dayOffset)) {
			return Math.max(0, serviceLike.dayOffset);
		}
		if (typeof serviceLike?.absoluteMinutes === "number" && !Number.isNaN(serviceLike.absoluteMinutes)) {
			return Math.max(0, Math.floor(serviceLike.absoluteMinutes / 1440));
		}
		return serviceLike?.start_date ? getDateDayOffset(serviceLike.start_date) : 0;
	};

	const crossesMidnight = (startTime, endTime) => {
		if (!startTime) return false;
		const startMinutes = convertToMinutes(startTime);
		const endMinutes = endTime ? convertToMinutes(endTime) : null;

		// If start_time is "00:00" or later (meaning it's already the next day), it crosses midnight
		if (startTime === "00:00" || startMinutes === 0) {
			return true;
		}

		if (endMinutes === 0) {
			return true;
		}

		// If end_time is less than start_time, it crossed midnight
		if (endMinutes !== null && endMinutes < startMinutes) {
			return true;
		}

		return false;
	};

	const deriveDateRange = (startDateCandidate, startTime, endTime, explicitDayOffset = null) => {
		let start_date = normalizeDateString(startDateCandidate);
		let end_date = start_date;

		if (typeof explicitDayOffset === "number" && explicitDayOffset !== 0) {
			start_date = addDaysToDateString(start_date, explicitDayOffset);
			end_date = crossesMidnight(startTime, endTime)
				? addDaysToDateString(start_date, 1)
				: start_date;
		} else if (startTime === "00:00" || (startTime && convertToMinutes(startTime) === 0)) {
			start_date = addDaysToDateString(start_date, 1);
			end_date = start_date; // Both dates are the same (next day)
		} else if (crossesMidnight(startTime, endTime)) {
			// Service crosses midnight (but doesn't start at 00:00)
			// Only increment end_date, start_date stays the same
			end_date = addDaysToDateString(start_date, 1);
		} else {
			// Service doesn't cross midnight, both dates are the same
			end_date = start_date;
		}

		return { start_date, end_date };
	};

	// Helper function to calculate cross_day_booking_item based on dates and times
	const calculateCrossDayBookingItem = (startDate, endDate, startTime, endTime) => {
		if (!startDate || !endDate || startDate === endDate) {
			return null;
		}

		// A service ending exactly at "00:00" has zero real duration on end_date — it's just the
		// date rolling over at the boundary, not a genuine split with presence on both days.
		if (convertToMinutes(endTime) === 0) {
			return null;
		}

		const startMinutes = convertToMinutes(startTime);
		const endMinutes = convertToMinutes(endTime);
		const minutesInDay = 24 * 60;

		// Calculate duration for first segment (from start_time to 00:00)
		const firstSegmentDuration = minutesInDay - startMinutes;

		// Calculate duration for second segment (from 00:00 to end_time)
		const secondSegmentDuration = endMinutes;

		// First segment: from start_time to 00:00 on start_date
		const firstSegment = {
			start_time: startTime,
			end_time: "00:00",
			duration: firstSegmentDuration,
			start_date: startDate,
			end_date: startDate,
		};

		// Second segment: from 00:00 to end_time on end_date
		const secondSegment = {
			start_time: "00:00",
			end_time: endTime,
			duration: secondSegmentDuration,
			start_date: endDate,
			end_date: endDate,
		};

		return [firstSegment, secondSegment];
	};

	const syncServicesWithBaseDate = (dateObj, options = {}) => {
		const { forceNormalizeToSameDay = false, firstServiceDayOffsetOverride = null } = options;
		const baseStr = formatDateToDDMMYYYY(dateObj);
		const rebuildServiceChain = (services = []) => {
			let currentDateStr = baseStr;
			return (Array.isArray(services) ? services : []).map((service, index) => {
			const startTime =
				service.start_time ||
				selectedTime?.value ||
				bookingData?.time?.value ||
				"00:00";
			const duration = Number(service.duration || 0);
			const endTime = service.end_time || calculateEndTime(startTime, duration);
			const explicitDayOffset = index === 0
				? (
					forceNormalizeToSameDay
						? 0
						: typeof firstServiceDayOffsetOverride === "number" && !Number.isNaN(firstServiceDayOffsetOverride)
						? Math.max(0, firstServiceDayOffsetOverride)
						: typeof service?.dayOffset === "number" && !Number.isNaN(service.dayOffset)
						? Math.max(0, service.dayOffset)
						: (
							typeof service?.absoluteMinutes === "number" && !Number.isNaN(service.absoluteMinutes)
								? Math.max(0, Math.floor(service.absoluteMinutes / 1440))
								: (
									service?.start_date
										? getDateDayOffset(service.start_date)
										: getTimeDayOffset(selectedTime || bookingData?.time)
								)
						)
				)
				: 0;

			const dateRange = deriveDateRange(currentDateStr, startTime, endTime, explicitDayOffset);
			// Update currentDateStr for next iteration. dateRange.end_date already correctly
			// reflects a midnight rollover (including services ending exactly at "00:00").
			currentDateStr = dateRange.end_date;

			// Calculate cross_day_booking_item based on dates and times
			const crossDayItem = calculateCrossDayBookingItem(
				dateRange.start_date,
				dateRange.end_date,
				startTime,
				endTime
			);

			return {
				...service,
				start_date: dateRange.start_date,
				end_date: dateRange.end_date,
				cross_day_booking_item: crossDayItem,
				is_calendar_expansion: forceNormalizeToSameDay
					? 0
					: (explicitDayOffset > 0 ? 1 : (service?.is_calendar_expansion ?? 0)),
				...buildServiceTimelineMeta(dateRange.start_date, startTime, baseStr),
			};
			});
		};

		if (Array.isArray(selectedServices) && selectedServices.length > 0) {
			setSelectedServices(rebuildServiceChain(selectedServices));
		}

		if (Array.isArray(paxes) && paxes.length > 0) {
			setPaxes((prev) => (Array.isArray(prev)
				? prev.map((pax) => ({
					...pax,
					selectedServices: rebuildServiceChain(pax?.selectedServices || []),
				}))
				: prev
			));
		}
	};

	useEffect(() => {
		if (selectedTime && selectedTime.value) {
			setLocalSelectedTime(normalizeTimeSelectionObject(selectedTime));
		}
	}, [
		selectedTime,
		selectedTime?.value,
		selectedTime?.label,
		selectedTime?.dayOffset,
		selectedTime?.absoluteMinutes,
	]);

	useEffect(() => {
		if (fromEdit) return;
		if (!Array.isArray(selectedServices) || selectedServices.length === 0) return;
		const firstStart = selectedServices[0]?.start_time;
		if (!firstStart) return;
		const firstService = selectedServices[0];
		const dayOffset = getServiceDisplayDayOffset(firstService);
		const absoluteMinutes =
			typeof firstService?.absoluteMinutes === "number" && !Number.isNaN(firstService.absoluteMinutes)
				? firstService.absoluteMinutes
				: null;
		const synced = buildTimeSelection(firstStart, dayOffset, "", absoluteMinutes);
		if (!synced) return;
		if (
			selectedTime?.value === synced.value &&
			selectedTime?.label === synced.label &&
			selectedTime?.dayOffset === synced.dayOffset &&
			selectedTime?.absoluteMinutes === synced.absoluteMinutes
		) {
			return;
		}
		setSelectedTime(synced);
		setLocalSelectedTime(synced);
	}, [selectedServices, selectedTime?.value, selectedTime?.label, selectedTime?.dayOffset, selectedTime?.absoluteMinutes, setSelectedTime]);



	// Create mode: react to parent initialTime changes. Edit mode uses one-time didInitFromEdit init only.
	useEffect(() => {
		if (fromEdit) return;
		if (initialTime) {
			const normalizedInitialTime = normalizeTimeSelectionObject(initialTime);
			setSelectedTime(normalizedInitialTime);
			setLocalSelectedTime(normalizedInitialTime);
		}
	}, [initialTime, setSelectedTime, fromEdit]);

	const displaySelectedTime = useMemo(
		() => normalizeTimeSelectionObject(localSelectedTime || selectedTime),
		[localSelectedTime, selectedTime]
	);

	const shouldDelayTimeDisplay = useMemo(
		() => (
			Number(calendarDateSyncSignal?.requestId) > 0 && calendarDateSyncSignal?.status === "pending" && calendarDateSyncSignal?.mode === (fromEdit ? "edit" : "create")
		),
		[calendarDateSyncSignal?.mode, calendarDateSyncSignal?.requestId, calendarDateSyncSignal?.status, fromEdit]
	);

	const displayedTimeLabel = formatTimeLabelForDisplay(
		shouldDelayTimeDisplay
			? (lastStableDisplayedTime || displaySelectedTime?.label || "")
			: (displaySelectedTime?.label || "")
	);

	useEffect(() => {
		if (shouldDelayTimeDisplay) return;
		if (!displaySelectedTime?.label) return;
		setLastStableDisplayedTime(formatTimeLabelForDisplay(displaySelectedTime.label));
	}, [displaySelectedTime?.label, shouldDelayTimeDisplay]);

	const displaySelectedDate = useMemo(() => {
		if (selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())) {
			return selectedDate;
		}
		const normalizedInitialDate = normalizeDateInput(initialDate);
		if (normalizedInitialDate) return normalizedInitialDate;
		return getSelectedDateObject();
	}, [selectedDate, initialDate]);

	const isOutletExtended = useMemo(
		() => getOutletExtendedFlagForDate(selectedOutletData?.timing, displaySelectedDate || selectedDate),
		[selectedOutletData?.timing, displaySelectedDate, selectedDate]
	);

	const timeOptions = useMemo(
		() => generateTimeOptions(isOutletExtended),
		[isOutletExtended]
	);

	const matchedTimeOption = useMemo(
		() => findMatchingTimeOption(displaySelectedTime, timeOptions),
		[displaySelectedTime, timeOptions]
	);

	const dropdownSelectedValue = formatTimeLabelForDisplay(
		matchedTimeOption
			? (typeof matchedTimeOption === "string" ? matchedTimeOption : matchedTimeOption.label)
			: displayedTimeLabel
	);
	
	// Set initial values once in edit mode
	useEffect(() => {
		if (!fromEdit) return;
		if (didInitFromEdit) return;

		if (initialDate) {
			setSelectedDate(initialDate);
		}
		if (initialTime) {
			const normalizedInitialTime = normalizeTimeSelectionObject(initialTime);
			setSelectedTime(normalizedInitialTime);
			setLocalSelectedTime(normalizedInitialTime);
		}
		setDidInitFromEdit(true);
	}, [fromEdit, initialDate, initialTime, didInitFromEdit, setSelectedDate, setSelectedTime]);

	// For new bookings, initialize once from bookingData if needed
	useEffect(() => {
		if (fromEdit) return;
		if (bookingData?.time && !selectedTime?.label) {
			const normalizedBookingTime = normalizeTimeSelectionObject(bookingData.time);
			setSelectedTime(normalizedBookingTime);
			setLocalSelectedTime(normalizedBookingTime);
		}
	}, [fromEdit, bookingData, selectedTime?.label, setSelectedTime]);



	// Initialize outlet info
	// Fetch outlet type ID when outlet changes
	useEffect(() => {
		if (selectedOutlet) {
			const matchedOutlet = outletList.find(
				(o) => o.id === selectedOutlet
			);
			if (matchedOutlet) {
				setSelectedOutletTypeID(matchedOutlet.outlet_type_id);
			}
		}
	}, [outletList, selectedOutlet]);

	const handleDateChange = (date) => {
		if (!date || isNaN(new Date(date))) return;
		closeTaggingDropdowns();
		const nextDateStr = formatDateToDDMMYYYY(date);
		// Same calendar day: skip setSelectedDate / sync. A new Date for the same day
		// still retriggers Home's table loader, which then never clears (booking fetch skipped).
		const currentDateStr = selectedDate ? formatDateToDDMMYYYY(selectedDate) : null;
		if (nextDateStr && currentDateStr && nextDateStr === currentDateStr) {
			setShowCalendar(false);
			return;
		}
		const effectiveTimeSelection =
			localSelectedTime?.value
				? localSelectedTime
				: (selectedTime?.value ? selectedTime : null);
		const hasServicesForDateSync =
			(Array.isArray(selectedServices) && selectedServices.length > 0) ||
			(Array.isArray(paxes) && paxes.some((pax) => Array.isArray(pax?.selectedServices) && pax.selectedServices.length > 0));
		const normalizedTimeSelectionForNextDate = (() => {
			if (!effectiveTimeSelection?.value) return null;
			const normalizedValue = normalizeTimeValue(effectiveTimeSelection.value);
			// Changing the calendar date makes that date the new base day — reset dayOffset
			// so leftover +1 timeline state from the previous date is not preserved.
			return buildTimeSelection(
				normalizedValue,
				0,
				"",
				convertToMinutes(normalizedValue)
			);
		})();
		setShowCalendar(false);
		logEditDateDebug("handleDateChange:start", {
			nextDateStr,
			selectedDate: selectedDate ? formatDateToDDMMYYYY(selectedDate) : null,
			selectedTime: selectedTime?.value || null,
			serviceDates: (selectedServices || []).map((service) => ({
				counterKey: service?.counterKey,
				start_time: service?.start_time,
				end_time: service?.end_time,
				start_date: service?.start_date,
				end_date: service?.end_date,
			})),
		});
		sessionStorage.setItem("skipCurrentTimeAutoScroll", "1");
		setCalendarDateSyncSignal((prev) => ({
			requestId: Number(prev?.requestId || 0) + 1,
			mode: fromEdit ? "edit" : "create",
			status: hasServicesForDateSync && effectiveTimeSelection?.value ? "pending" : "ready",
			syncIntent: "date",
			date: nextDateStr,
			refreshKey: Number(prev?.refreshKey || 0) + 1,
		}));
		setSelectedDate(date);
		setSelectedTime((prev) => {
			if (!prev || !prev.value) return null;
			return normalizedTimeSelectionForNextDate || prev;
		});
		if (normalizedTimeSelectionForNextDate) {
			setLocalSelectedTime(normalizedTimeSelectionForNextDate);
		}

		if (fromEdit) {
			onDateChange?.(date);
		} else {
			setBookingData((prev) => ({
				...prev,
				date: date.toString(),
			}));
		}

		syncServicesWithBaseDate(date, {
			forceNormalizeToSameDay: true,
			firstServiceDayOffsetOverride: 0,
		});
		logEditDateDebug("handleDateChange:afterSyncServicesWithBaseDate", {
			nextDateStr,
		});
		setIsDateChange(true);
		setHitTemporaryBookingApi(true);

		setItemData((prev) => ({
			...prev,
			service_at: (normalizedTimeSelectionForNextDate || effectiveTimeSelection)?.value
				? `${getServiceAtDateForSelection(date, normalizedTimeSelectionForNextDate || effectiveTimeSelection)} ${(normalizedTimeSelectionForNextDate || effectiveTimeSelection).value}`
				: prev?.service_at,
		}));
	};

	const handleDateChangeRef = useRef(handleDateChange);
	handleDateChangeRef.current = handleDateChange;
	const lastProcessedHeaderDateSyncRef = useRef(null);

	useEffect(() => {
		const expectedMode = fromEdit ? "edit" : "create";
		if (dateSyncState?.mode !== expectedMode) return;
		if (dateSyncState?.status !== "pending") return;
		if (!dateSyncState?.requestId || !dateSyncState?.date) return;
		if (lastProcessedHeaderDateSyncRef.current === dateSyncState.requestId) return;

		const nextDate = dateSyncState.date instanceof Date
			? dateSyncState.date
			: new Date(dateSyncState.date);
		if (Number.isNaN(nextDate.getTime())) return;

		lastProcessedHeaderDateSyncRef.current = dateSyncState.requestId;
		handleDateChangeRef.current(nextDate);
		setDateSyncState((prev) => ({
			...prev,
			status: "complete",
		}));
	}, [dateSyncState?.requestId, dateSyncState?.status, dateSyncState?.mode, dateSyncState?.date, fromEdit, setDateSyncState]);

	const handleTimeSelect = (selected) => {
		if (
			!selected ||
			!selected.label ||
			typeof selected.label !== "string"
		) {
			console.error("Invalid time format received:", selected);
			return;
		}
		closeTaggingDropdowns();
		const optionDayOffset = getTimeDayOffset(selected);
		const time24h = convertTo24HourFormat(selected.value);
		const firstService = Array.isArray(selectedServices)
			? selectedServices.find((svc) => !svc?.isSplitSegment) || selectedServices[0]
			: null;
		const selectedDateStr = formatSelectedDateString();
		const useNearest = isServiceEligibleForCrossDayTimeSelection(firstService, selectedDateStr, null);
		let dayOffset = optionDayOffset;
		let absoluteMinutes =
			typeof selected?.absoluteMinutes === "number" && !Number.isNaN(selected.absoluteMinutes)
				? selected.absoluteMinutes
				: null;
		if (useNearest) {
			const referenceAbsoluteMinutes = getViewRelativeAbsoluteStartMinutes(
				firstService,
				selectedDateStr,
				(typeof firstService?.absoluteMinutes === "number" && !Number.isNaN(firstService.absoluteMinutes)
					? firstService.absoluteMinutes
					: null) ??
				(typeof selectedTime?.absoluteMinutes === "number" && !Number.isNaN(selectedTime.absoluteMinutes)
					? selectedTime.absoluteMinutes
					: null) ??
				(typeof localSelectedTime?.absoluteMinutes === "number" && !Number.isNaN(localSelectedTime.absoluteMinutes)
					? localSelectedTime.absoluteMinutes
					: null)
			);
			const nearestSelection = resolveNearestTimeSelection(
				time24h,
				referenceAbsoluteMinutes,
				optionDayOffset,
				selected.label
			);
			dayOffset = nearestSelection?.dayOffset ?? optionDayOffset;
			absoluteMinutes =
				typeof nearestSelection?.absoluteMinutes === "number" && !Number.isNaN(nearestSelection.absoluteMinutes)
					? nearestSelection.absoluteMinutes
					: absoluteMinutes;
		} else if (optionDayOffset <= 0) {
			// Same-day booking: keep early morning on selected day (ignore nearest → +1).
			dayOffset = 0;
			absoluteMinutes = convertToMinutes(time24h);
		}
		const formattedTime = buildCanonicalTimeSelection(time24h, dayOffset, selected.label, absoluteMinutes);
		if (!formattedTime) return;

		setSelectedTime(formattedTime); // ✅ Store correctly formatted time
		setLocalSelectedTime(formattedTime); // ✅ Store locally as well
		if (fromEdit) {
			onTimeChange?.(formattedTime);
		}
		setBookingData((prev) => ({
			...prev,
			time: formattedTime,
		}));
		
		setItemData((prev) => ({
			...prev,
			service_at: `${getServiceAtDateForSelection(selectedDate, formattedTime)} ${time24h}`,
		}));
		
		// Create flow: schedule main-customer chain via shared algorithm (edit uses onTimeChange).
		if (!fromEdit && selectedServices.length > 0) {
			const baseStartDateStr = formatSelectedDateString();
			const firstKey = resolveServiceScheduleKey(selectedServices[0]);
			const newAbsoluteStartMinutes =
				typeof formattedTime?.absoluteMinutes === "number" && !Number.isNaN(formattedTime.absoluteMinutes)
					? formattedTime.absoluteMinutes
					: (dayOffset * 1440) + convertToMinutes(time24h);

			const { services: scheduledServices, affectedIndices } = scheduleServiceMove({
				services: ensureServiceLinkedFlags(selectedServices),
				movedKey: firstKey,
				newAbsoluteStartMinutes,
			});

			const affectedSet = new Set(affectedIndices);
			const updatedServices = scheduledServices.map((svc, index) => {
				if (!affectedSet.has(index) && index !== 0) {
					return svc;
				}
				const startTime = svc.start_time;
				const endTime = svc.end_time || calculateEndTime(startTime, Number(svc.duration) || 0);
				const signedDayOffset =
					typeof svc.absoluteMinutes === "number" && !Number.isNaN(svc.absoluteMinutes)
						? Math.floor(svc.absoluteMinutes / 1440)
						: (svc.dayOffset ?? 0);
				const dateRange = deriveDateRange(
					baseStartDateStr,
					startTime,
					endTime,
					signedDayOffset
				);
				const crossDayItem = calculateCrossDayBookingItem(
					dateRange.start_date,
					dateRange.end_date,
					startTime,
					endTime
				);
				const isMemberRateAppliedValue =
					isTimeBefore6PM(startTime) && isMembershipApplied && !publicHoliday && isWeekday(dateRange.start_date);
				const timelineMeta = buildServiceTimelineMeta(dateRange.start_date, startTime, baseStartDateStr);

				return {
					...svc,
					start_date: dateRange.start_date,
					end_date: dateRange.end_date,
					dayOffset: signedDayOffset,
					absoluteMinutes:
						typeof svc.absoluteMinutes === "number" && !Number.isNaN(svc.absoluteMinutes)
							? svc.absoluteMinutes
							: timelineMeta.absoluteMinutes,
					cross_day_booking_item: crossDayItem,
					room_segments: svc.room_segments
						? svc.room_segments.map((segment) => ({
							...segment,
							start_time: startTime,
							end_time: endTime,
						}))
						: null,
					isBefore6pm: isTimeBefore6PM(startTime),
					isMemberRateApplied: isMemberRateAppliedValue,
					price: getServicePriceFromPriceRate(svc, isMemberRateAppliedValue),
				};
			});

			setSelectedServices(updatedServices);
		}

		// Create booking: services are updated synchronously above — signal ready immediately
		// so the calendar reprojects and scrolls without waiting on the heavy date-sync API path.
		if (!fromEdit) {
			const dateStr = formatSelectedDateString();
			setCalendarDateSyncSignal((prev) => ({
				requestId: Number(prev?.requestId || 0) + 1,
				mode: "create",
				status: "ready",
				syncIntent: "time",
				date: dateStr,
				refreshKey: Number(prev?.refreshKey || 0) + 1,
			}));
		}
		
		// ✅ THIS LINE IS CRUCIAL
		setBookingChanged(true);
		setIsTimeChange(true);
		setHitTemporaryBookingApi(true);
	};

	useEffect(() => {
		if (selectedOutlet) {
			setItemData((prev) => ({ ...prev, outlet: selectedOutlet }));
		}
		// Also update editedItemData if in edit mode
		if (fromEdit) {
			setEditedItemData((prev) => ({ ...prev, outlet: selectedOutlet }));
		}
	}, [selectedOutlet, fromEdit, setEditedItemData]);

	useEffect(() => {
		if (selectedOutletTypeID) {
			setItemData((prev) => ({
				...prev,
				outlet_type: selectedOutletTypeID,
			}));
			// Also update editedItemData if in edit mode
			if (fromEdit) {
				setEditedItemData((prev) => ({
					...prev,
					outlet_type: selectedOutletTypeID,
				}));
			}
		}
	}, [selectedOutletTypeID, fromEdit, setEditedItemData]);


	const ReadOnlyInput = React.forwardRef(({ value, onClick, disabled }, ref) => (
		<input
		  ref={ref}
		  value={value}
		  onClick={onClick}
		  readOnly 
		  className={`${styles.formControl} ${styles.readOnly} ${disabled ? "disabled" : ""} form-control rightsidebar-date-input`}
		  disabled={disabled}
		/>
	  ));


	return (
		<div className={styles.availableFilterHeader}>
			<div className={styles.outletName}>
				<label className={styles.label}>Outlet</label>
				<p>{selectedOutletName}</p>
			</div>
			<div className={styles.halfDiv}>
				<div className="datePickerWrapper rightsidebar position-relative">
					<label className={styles.label}>On</label>
					<div className={`${styles.dateContainer} ${(isBookingPaymentInProgress || isBookingCompleted) ? styles.disabled : ""}`} onClick={(e) => {
					if (isBookingPaymentInProgress) {
						onPaymentInProgressAttempt?.();
						return;
					}
					if (isBookingCompleted) return;
					// Only toggle calendar if click is not inside the calendar dropdown
					if (!e.target.closest('.react-datepicker')) {
						closeTaggingDropdowns();
						setShowCalendar(!showCalendar);
					}
					}}>
						<DatePicker
						locale="en"
						selected={displaySelectedDate}
						onChange={handleDateChange}
						dateFormat="EEE, MMM d" 
						open={showCalendar}
						onClickOutside={() => setShowCalendar(false)}
						className={`${styles.formControl} ${styles.readOnly} form-control `}
						calendarClassName={styles.customDatePicker}
						customInput={<ReadOnlyInput disabled={isBookingPaymentInProgress || isBookingCompleted} />}
						/>
					</div>
				</div>
				{formErrors.date && (
					<p className={styles.errorMessage}>{formErrors.date}</p>
				)}
				<div
					onClickCapture={() => {
						if (isBookingPaymentInProgress) {
							closeTaggingDropdowns();
							onPaymentInProgressAttempt?.();
						}
					}}
				>
					<label className={styles.label}>At</label>
					<DropdownNoLabel
						options={timeOptions}
						selectedLabel={displayedTimeLabel}
						selectedValue={dropdownSelectedValue}
						onSelect={handleTimeSelect}
						onDropdownToggle={(isOpen) => {
							if (isOpen) closeTaggingDropdowns();
						}}
						dropdownClass={`${styles.dropdownNoBg} dropdownNoBg`}
						labelText=""
						placeholder="Time Slot"
						placeholderClass="greyPlaceholder"
						// disabled={isBookingPaymentInProgress}
					/>
				</div>
				{formErrors.time && (
					<p className={styles.errorMessage}>{formErrors.time}</p>
				)}
			</div>
		</div>
	);
};

export default DateTimeOutletSelector;